// Data hook for FulfillmentOrdersPanel — retail-side customer-order pipeline.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  listFulfillmentOrders,
  type FulfillmentOrderRow,
} from "@/lib/fulfillment/fulfillmentApi";
import type { OpsOrderIntakeStatus } from "@/lib/orderIntakeClient";

export type FulfillmentOrdersView = "open" | "accepted" | "all";

const OPEN_STATUSES: OpsOrderIntakeStatus[] = ["pending_review", "on_hold"];
const ACCEPTED_STATUSES: OpsOrderIntakeStatus[] = ["accepted"];

export type FulfillmentOrdersModel = {
  orders: FulfillmentOrderRow[];
  total: number | null;
  page: number;
  view: FulfillmentOrdersView;
  setView: (v: FulfillmentOrdersView) => void;
  setPage: (n: number | ((p: number) => number)) => void;
  busy: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  pageSize: number;
};

const PAGE_SIZE = 10;

export function useFulfillmentOrders(): FulfillmentOrdersModel {
  const [orders, setOrders] = useState<FulfillmentOrderRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [view, setView] = useState<FulfillmentOrdersView>("open");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async () => {
    if (inFlight.current) return inFlight.current;
    setBusy(true);
    setError(null);
    const status =
      view === "open" ? OPEN_STATUSES : view === "accepted" ? ACCEPTED_STATUSES : undefined;
    const p = (async () => {
      const r = await listFulfillmentOrders({
        status,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      if (!r.ok) {
        setError(r.error);
        setOrders([]);
        setTotal(null);
        return;
      }
      setOrders(r.data.orders);
      setTotal(r.data.total);
    })()
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Failed to load orders.");
        setOrders([]);
        setTotal(null);
      })
      .finally(() => {
        setBusy(false);
        inFlight.current = null;
      });
    inFlight.current = p;
    return p;
  }, [page, view]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setPage(0);
  }, [view]);

  return {
    orders,
    total,
    page,
    view,
    setView,
    setPage,
    busy,
    error,
    refresh,
    pageSize: PAGE_SIZE,
  };
}
